import React from 'react';
import { Button } from './ui/button';
import { Phone, ArrowRight, Star } from 'lucide-react';
import { getImageUrl } from '../lib/imageUtils';

const Hero = ({ data = {} }) => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const image1 = getImageUrl(data.image_1_url, 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400');
  const image2 = getImageUrl(data.image_2_url, 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400');

  return (
    <section id="home" className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-20 pb-32 overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-100 rounded-full filter blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md border border-blue-100" data-testid="hero-badge">
              <div className="flex items-center space-x-1">
                {[1,2,3,4,5].map((s) => (
                  <Star key={s} size={16} className="text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-700">{data.badge_text || '4.6/5 on Google Reviews'}</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight" data-testid="hero-headline">
              {data.headline_part1 || 'Premium Pipes & Valves'}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800"> {data.headline_part2 || 'Solutions'}</span>
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed" data-testid="hero-description">
              {data.description || 'Your trusted partner for high-quality piping systems.'}
            </p>

            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-xl shadow-lg">
              <p className="text-sm font-medium mb-2">{data.info_box_title || 'Serving Contractors & MEP Consultants'}</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span>{data.info_point_1 || 'Premium Quality Products'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span>{data.info_point_2 || 'Competitive Pricing'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span>{data.info_point_3 || 'Expert Consultation'}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button 
                onClick={scrollToContact}
                size="lg" 
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 shadow-lg hover:shadow-xl transition-all"
                data-testid="hero-cta-primary"
              >
                {data.cta_primary || 'Request Quote'} <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                onClick={() => window.location.href = 'tel:05224102175'}
                size="lg" 
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8"
                data-testid="hero-cta-secondary"
              >
                <Phone className="mr-2" size={20} /> {data.cta_secondary || 'Call Now'}
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-4xl font-bold text-blue-900">{data.stat_1_value || '15+'}</div>
                  <div className="text-gray-700 font-medium">{data.stat_1_label || 'Years Experience'}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-lg aspect-square flex items-center justify-center border-4 border-blue-100 hover:border-blue-200 transition-colors">
                  <img src={image1} alt="Pipes" className="w-full h-full object-cover rounded-xl" />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="bg-white p-6 rounded-2xl shadow-lg aspect-square flex items-center justify-center border-4 border-yellow-100 hover:border-yellow-200 transition-colors">
                  <img src={image2} alt="Industrial Valves" className="w-full h-full object-cover rounded-xl" />
                </div>
                <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-4xl font-bold text-yellow-900">{data.stat_2_value || '1000+'}</div>
                  <div className="text-gray-700 font-medium">{data.stat_2_label || 'Happy Clients'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
